/**
 * Registered schools (config-in-code)
 * -----------------------------------
 * Temporary source of truth for the school-representative referral flow.
 *
 * The school a representative selects determines:
 *   - which school/address is attached to the referral, and
 *   - whether the child is tagged as `primary` or `secondary`, which in turn
 *     drives DOB age bands (primary 4-11, secondary 11-16) and whether the HFP
 *     and proceed-with-triage steps are shown (secondary skips them).
 *
 * This intentionally lives in code for now. When the canonical schools registry
 * exists in Firestore (`organisations/{orgId}/schools`), replace the lookups
 * below with a fetch; the shape (`id`, `name`, `type`, `address`) is the contract
 * the selector and tagging logic depend on, so keep it stable.
 */

export const SCHOOL_TYPES = Object.freeze({
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
});

/**
 * @typedef {Object} RegisteredSchool
 * @property {string} id        Stable identifier stored on the referral.
 * @property {string} name      Display name shown in the selector.
 * @property {'primary'|'secondary'} type  Drives DOB bands and HFP/triage gating.
 * @property {{ line1: string, city: string, postcode: string }} address
 */

/** @type {RegisteredSchool[]} */
export const REGISTERED_SCHOOLS = [
  {
    id: 'test-primary-school',
    name: 'Test Primary School',
    type: SCHOOL_TYPES.PRIMARY,
    address: { line1: '1 Test Primary Lane', city: 'Manchester', postcode: 'M1 1AA' },
  },
  {
    id: 'test-secondary-school',
    name: 'Test Secondary School',
    type: SCHOOL_TYPES.SECONDARY,
    address: { line1: '2 Test Secondary Road', city: 'Manchester', postcode: 'M2 2BB' },
  },
];

/** Schools sorted alphabetically by name (for the selector dropdown). */
export function listRegisteredSchools() {
  return [...REGISTERED_SCHOOLS].sort((a, b) => a.name.localeCompare(b.name));
}

/** Look up a single school by its id; returns null if not registered. */
export function getSchoolById(id) {
  if (!id) return null;
  return REGISTERED_SCHOOLS.find((s) => s.id === id) || null;
}

/** Resolve the school type ('primary' | 'secondary') for a selected school id. */
export function getSchoolType(id) {
  return getSchoolById(id)?.type || null;
}

/** True when the selected school is a secondary school (skips HFP + triage step). */
export function isSecondarySchool(id) {
  return getSchoolType(id) === SCHOOL_TYPES.SECONDARY;
}

/** Format a school's address as a single human-readable line for display/review. */
export function formatSchoolAddress(school) {
  if (!school || !school.address) return '';
  const { line1, city, postcode } = school.address;
  return [line1, city, postcode].filter(Boolean).join(', ');
}
